import { AutomationInputTypes, EffectTypes, ExpressionTypes } from 'models/enums/Automation';

export const automationValueExpressionTypes: Model.Enum.Automation.ExpressionTypes[] = [
  // ExpressionTypes.ValueJson,
  ExpressionTypes.ValueNumeric,
  ExpressionTypes.ValueString,
  ExpressionTypes.ValueBoolean,
  ExpressionTypes.ValueDateTime,
  ExpressionTypes.ValueTimeSpan,
  ExpressionTypes.ValueProperty,
];

const literalExpressionTypes = new Set<Model.Enum.Automation.ExpressionTypes>([
  // ExpressionTypes.ValueJson,
  ExpressionTypes.ValueNumeric,
  ExpressionTypes.ValueString,
  ExpressionTypes.ValueBoolean,
  ExpressionTypes.ValueDateTime,
  ExpressionTypes.ValueTimeSpan,
]);

export function isLiteralExpressionType(type?: Model.Enum.Automation.ExpressionTypes) {
  return Boolean(type && literalExpressionTypes.has(type));
}

export function isValuePropertyExpressionType(type?: Model.Enum.Automation.ExpressionTypes) {
  return type === ExpressionTypes.ValueProperty;
}

export function isValueExpressionType(type?: Model.Enum.Automation.ExpressionTypes) {
  return isLiteralExpressionType(type) || isValuePropertyExpressionType(type);
}

export const automationExpressionValueInputTypes: Partial<
  Record<Model.Enum.Automation.ExpressionTypes, Model.Enum.Automation.AutomationInputTypes>
> = {
  [ExpressionTypes.ValueNumeric]: AutomationInputTypes.Number,
  [ExpressionTypes.ValueString]: AutomationInputTypes.Text,
  [ExpressionTypes.ValueBoolean]: AutomationInputTypes.Boolean,
  [ExpressionTypes.ValueDateTime]: AutomationInputTypes.DateTime,
  [ExpressionTypes.ValueTimeSpan]: AutomationInputTypes.TimeSpan,
  [ExpressionTypes.ValueProperty]: AutomationInputTypes.Property,
  // [ExpressionTypes.ValueJson]: AutomationInputTypes.Json,
};

export function getAutomationExpressionValueInputType(type?: Model.Enum.Automation.ExpressionTypes) {
  return type ? automationExpressionValueInputTypes[type] : undefined;
}

export function getAutomationExpressionTypeDefaultValue(
  type: Model.Enum.Automation.ExpressionTypes | undefined
) {
  switch (getAutomationExpressionValueInputType(type)) {
    case AutomationInputTypes.Boolean:
      return 'false';
    case AutomationInputTypes.Property:
      return null;
    case AutomationInputTypes.Number:
    case AutomationInputTypes.Text:
    case AutomationInputTypes.DateTime:
    case AutomationInputTypes.TimeSpan:
    case AutomationInputTypes.Json:
      return '';
    default:
      return undefined;
  }
}

export type AutomationOption = {
  id: string | number | boolean;
  title: string;
};

export type AutomationPropertyMetadata = {
  supportedExpressions: Model.Enum.Automation.ExpressionTypes[];
  inputType?: Model.Enum.Automation.AutomationInputTypes;
  options?: AutomationOption[];
  containsValuePath?: string;
  defaultValue?: string | number | boolean;
  getDefaultValue?: (expressionType: Model.Enum.Automation.ExpressionTypes) => string | number | boolean;
  formatValue?: (value: any) => string;
  serializeValue?: (value: any) => string;
  parseValue?: (value: string) => any;
  onValueChange?: (value: any) => void;
};

export type AutomationAssignEffectMetadata = {
  type: 'assign';
  propertyPath: string;
  options?: AutomationOption[];
  supportedEffectTypes?: Model.Enum.Automation.EffectTypes[];
};

export type AutomationPathIdEffectMetadata = {
  type: 'pathIdMultiSelect';
  propertyPathPrefix: string;
  options?: AutomationOption[];
  value?: string;
  buildPropertyPath: (id: string | number) => string;
  parseIdFromPropertyPath: (propertyPath: string) => string | number | null;
};

export type AutomationEffectMetadata = AutomationAssignEffectMetadata | AutomationPathIdEffectMetadata;

export function getAutomationEffectMetadataKey(effect: AutomationEffectMetadata) {
  return effect.type === 'assign' ? effect.propertyPath : effect.propertyPathPrefix;
}

export type AutomationRuleSchema = {
  expressionProperties: string[];
  effectProperties: string[];
  supportedEffectTypes?: Model.Enum.Automation.EffectTypes[];
  properties: Record<string, AutomationPropertyMetadata>;
  effects: AutomationEffectMetadata[];
  allowMultipleRules?: boolean;
  allowMultipleExpressions?: boolean;
  allowMultipleEffects?: boolean;
  readonly?: boolean;
  isPropertySelectorDisabled?: boolean;
  defaultExpression?: Partial<Omit<Model.Automation.IExpression, 'uid'>>;
  defaultEffects?: Model.Automation.IEffect[];
};

export type AutomationRuleLike = Model.Automation.IRule & {
  id?: number;
  isDeleted?: boolean;
  [key: string]: any;
};

export const defaultAutomationExpression: Omit<Model.Automation.IExpression, 'uid'> = {
  type: undefined,
  propertyPath: '',
  value: '',
};

export const createAssignEffect = (propertyPath: string, value = ''): Model.Automation.IEffect => ({
  type: EffectTypes.Assign,
  propertyPath,
  value,
});
